import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Heart, Brain, Baby, Eye, Monitor, Shield, ArrowRight, Check } from 'lucide-react'
import ultrasoundImg from '../assets/ultrasound.png'
import nuclearMedicineImg from '../assets/nuclearmedicine.png'
import catImg from '../assets/cat.png'

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const services = [
    {
      icon: Monitor,
      title: 'Nuclear Medicine',
      description: 'Advanced nuclear medicine imaging systems for precise diagnostic capabilities.',
      image: nuclearMedicineImg,
      features: [
        'SPECT scanning',
        'Radioactive tracers',
        'Molecular imaging',
        'Gamma camera systems',
        'Radiopharmaceuticals'
      ],
      applications: ['Hospitals', 'Nuclear medicine departments', 'Cancer centers', 'Research facilities']
    },
    {
      icon: Brain,
      title: 'CAT Scan',
      description: 'High-resolution computed tomography systems for comprehensive body imaging.',
      image: catImg,
      features: [
        'Multi-slice CT',
        '3D reconstruction',
        'Low-dose protocols',
        'Emergency imaging',
        'Cardiac CT',
        'Virtual colonoscopy'
      ],
      applications: ['Hospitals', 'Imaging centers', 'Emergency departments', 'Specialty clinics']
    },
    {
      icon: Heart,
      title: 'Ultrasound',
      description: 'State-of-the-art ultrasound systems for real-time diagnostic imaging.',
      image: ultrasoundImg,
      features: [
        'Real-time imaging',
        'Doppler analysis',
        '3D/4D capabilities',
        'Portable systems',
        'Abdominal imaging',
        'Vascular studies'
      ],
      applications: ['Hospitals', 'Clinics', 'Emergency departments', 'Specialty practices']
    },
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-100 to-gray-200 py-20">
        <div className="container-custom">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive ultrasound and radiology solutions designed to meet the diverse needs 
              of modern healthcare facilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group"
              >
                {/* Image Section */}
                <div className="relative h-48 bg-gradient-to-br from-primary-500 to-accent-400 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300"></div>
                  <div className="absolute top-4 left-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-600">
                          <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Applications</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.applications.map((app, appIndex) => (
                        <span
                          key={appIndex}
                          className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200 group">
                    Learn More
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Services 